import React, { useState, useEffect } from 'react';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import { AiOutlineClose } from 'react-icons/ai';
import * as validators from '../../util/validators';
import config from '../../config';
import {
  Form,
  FieldSelect,
  FieldSelectModern,
  FieldTextInput,
  LocationAutocompleteInputField,
} from '../../components';

import css from './CustomerSignupForms.css';

const identity = v => v;

const getLanguageTranslationId = langKey => {
  if (!langKey) return null;
  const capitalized = langKey.charAt(0).toUpperCase() + langKey.slice(1);
  return `SearchLanguages.language${capitalized}`;
};

const workedWithTranslationMap = {
  never: 'CustomerSignupForms.workedWithNever',
  couple_of_times: 'CustomerSignupForms.workedWithCoupleOfTimes',
  occasionally: 'CustomerSignupForms.workedWithOccasionally',
  often: 'CustomerSignupForms.workedWithOften',
};

const CustomerSignupStep2Form = props => {
  const { onSubmit, onPrev, initialValues, intl } = props;

  const [selectedLanguages, setSelectedLanguages] = useState(initialValues.languages || []);
  const [locationValue, setLocationValue] = useState(initialValues.location || null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
  }, []);

  useEffect(() => {
    setSelectedLanguages(initialValues.languages || []);
    setLocationValue(initialValues.location || null);
  }, [initialValues.languages, initialValues.location]);

  // Language helpers
  const languageOptions = config.custom.filters
    ? config.custom.filters.find(f => f.id === 'languages')?.config?.options || []
    : [];

  const getLabel = key => {
    const id = getLanguageTranslationId(key);
    return id ? intl.formatMessage({ id }) : key;
  };

  const languageSelectOptions = languageOptions
    .filter(opt => !selectedLanguages.includes(opt.key))
    .map(opt => ({ value: opt.key, label: getLabel(opt.key) }));

  const handleLanguageSelect = option => {
    if (!option) return;
    setSelectedLanguages([...selectedLanguages, option.value]);
  };

  const removeLanguage = langKey => {
    setSelectedLanguages(selectedLanguages.filter(l => l !== langKey));
  };

  // How did you hear about us
  const howDidYouHearLabel = intl.formatMessage({ id: 'CustomerSignupForms.howDidYouHearLabel' });
  const howDidYouHearPlaceholder = intl.formatMessage({
    id: 'CustomerSignupForms.howDidYouHearPlaceholder',
  });
  const howDidYouHearRequired = validators.required(
    intl.formatMessage({ id: 'SignupForm.howDidYouHearRequired' })
  );
  const howDidYouHearOtherLabel = intl.formatMessage({
    id: 'CustomerSignupForms.howDidYouHearOtherLabel',
  });
  const howDidYouHearOtherPlaceholder = intl.formatMessage({
    id: 'CustomerSignupForms.howDidYouHearOtherPlaceholder',
  });
  const howDidYouHearOtherRequired = validators.required(
    intl.formatMessage({ id: 'SignupForm.howDidYouHearOtherRequired' })
  );

  // location
  const locationRequiredMessage = intl.formatMessage({
    id: 'CustomerSignupForms.locationRequired',
  });

  // roleAtCompany
  const roleAtCompanyLabel = intl.formatMessage({ id: 'CustomerSignupForms.roleAtCompanyLabel' });
  const roleAtCompanyPlaceholder = intl.formatMessage({
    id: 'CustomerSignupForms.roleAtCompanyPlaceholder',
  });
  const roleAtCompanyRequired = validators.required(roleAtCompanyPlaceholder);

  // workedWithFreelancers
  const workedWithFreelancersLabel = intl.formatMessage({
    id: 'CustomerSignupForms.workedWithFreelancersLabel',
  });
  const workedWithFreelancersPlaceholder = intl.formatMessage({
    id: 'CustomerSignupForms.workedWithFreelancersPlaceholder',
  });
  const workedWithFreelancersRequired = validators.required(workedWithFreelancersPlaceholder);

  return (
    <FinalForm
      initialValues={{
        howDidYouHear: initialValues.howDidYouHear || '',
        howDidYouHearOther: initialValues.howDidYouHearOther || '',
        roleAtCompany: initialValues.roleAtCompany || '',
        workedWithFreelancers: initialValues.workedWithFreelancers || '',
        location: locationValue,
        languageSearch: null,
      }}
      onSubmit={values => {
        const { languageSearch, ...rest } = values;
        onSubmit({
          ...rest,
          languages: selectedLanguages,
        });
      }}
      render={fieldRenderProps => {
        const { handleSubmit, invalid, values, form } = fieldRenderProps;

        const handleBack = () => {
          const { languageSearch, ...rest } = form.getState().values;
          onPrev({
            ...rest,
            languages: selectedLanguages,
          });
        };

        return (
          <Form className={css.root} onSubmit={handleSubmit}>
            <div className={css.formFields}>
              {/* Languages */}
              <div>
                <FieldSelectModern
                  name="languageSearch"
                  id="languageSearch"
                  label={intl.formatMessage({ id: 'CustomerSignupForms.languagesLabel' })}
                  options={languageSelectOptions}
                  placeholder={intl.formatMessage({
                    id: 'CustomerSignupForms.languagesPlaceholder',
                  })}
                  isSearchable={true}
                  isClearable={false}
                  onChange={option => {
                    if (option) {
                      handleLanguageSelect(option);
                      form.change('languageSearch', null);
                    }
                  }}
                />
                {selectedLanguages.length > 0 ? (
                  <div className={css.languageTags}>
                    {selectedLanguages.map(lang => (
                      <span key={lang} className={css.languageTag}>
                        {getLabel(lang)}
                        <button
                          type="button"
                          className={css.languageTagRemove}
                          onClick={() => removeLanguage(lang)}
                        >
                          <AiOutlineClose className={css.languageTagRemoveIcon} />
                        </button>
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>

              {/* Location */}
              <div className={css.fieldGroup}>
                <label className={css.label}>
                  {intl.formatMessage({ id: 'CustomerSignupForms.locationLabel' })}
                </label>
                <div className={css.locationWrapper}>
                  <LocationAutocompleteInputField
                    name="location"
                    placeholder={intl.formatMessage({
                      id: 'CustomerSignupForms.locationPlaceholder',
                    })}
                    useDefaultPredictions={false}
                    format={identity}
                    valueFromForm={values.location}
                    validate={validators.autocompletePlaceSelected(locationRequiredMessage)}
                    predictionType="place"
                    onChange={val => {
                      setLocationValue(val);
                    }}
                  />
                </div>
              </div>

              {/* How did you hear about us */}
              <FieldSelect
                rootClassName={css.fieldSelect}
                id="howDidYouHear"
                name="howDidYouHear"
                label={howDidYouHearLabel}
                validate={howDidYouHearRequired}
              >
                <option value="">{howDidYouHearPlaceholder}</option>
                <option value="referral">
                  {intl.formatMessage({ id: 'CustomerSignupForms.howDidYouHearReferral' })}
                </option>
                <option value="ai">
                  {intl.formatMessage({ id: 'CustomerSignupForms.howDidYouHearAI' })}
                </option>
                <option value="searchEngine">
                  {intl.formatMessage({ id: 'CustomerSignupForms.howDidYouHearSearchEngine' })}
                </option>
                <option value="linkedin">
                  {intl.formatMessage({ id: 'CustomerSignupForms.howDidYouHearLinkedin' })}
                </option>
                <option value="tiktok">
                  {intl.formatMessage({ id: 'CustomerSignupForms.howDidYouHearTiktok' })}
                </option>
                <option value="instagram">
                  {intl.formatMessage({ id: 'CustomerSignupForms.howDidYouHearInstagram' })}
                </option>
                <option value="facebook">
                  {intl.formatMessage({ id: 'CustomerSignupForms.howDidYouHearFacebook' })}
                </option>
                <option value="x">
                  {intl.formatMessage({ id: 'CustomerSignupForms.howDidYouHearX' })}
                </option>
                <option value="email">
                  {intl.formatMessage({ id: 'CustomerSignupForms.howDidYouHearEmail' })}
                </option>
                <option value="phone">
                  {intl.formatMessage({ id: 'CustomerSignupForms.howDidYouHearPhone' })}
                </option>
                <option value="event">
                  {intl.formatMessage({ id: 'CustomerSignupForms.howDidYouHearEvent' })}
                </option>
                <option value="other">
                  {intl.formatMessage({ id: 'CustomerSignupForms.howDidYouHearOther' })}
                </option>
              </FieldSelect>

              {values.howDidYouHear === 'other' ? (
                <FieldTextInput
                  rootClassName={css.field}
                  type="text"
                  id="howDidYouHearOther"
                  name="howDidYouHearOther"
                  label={howDidYouHearOtherLabel}
                  placeholder={howDidYouHearOtherPlaceholder}
                  validate={howDidYouHearOtherRequired}
                />
              ) : null}

              {/* Your role */}
              <FieldTextInput
                rootClassName={css.field}
                type="text"
                id="roleAtCompany"
                name="roleAtCompany"
                label={roleAtCompanyLabel}
                placeholder={roleAtCompanyPlaceholder}
                validate={roleAtCompanyRequired}
              />

              {/* Have you worked with freelancers before? */}
              <FieldSelect
                rootClassName={css.fieldSelect}
                id="workedWithFreelancers"
                name="workedWithFreelancers"
                label={workedWithFreelancersLabel}
                validate={workedWithFreelancersRequired}
              >
                <option value="">{workedWithFreelancersPlaceholder}</option>
                {Object.entries(workedWithTranslationMap).map(([key, translationId]) => (
                  <option key={key} value={key}>
                    {intl.formatMessage({ id: translationId })}
                  </option>
                ))}
              </FieldSelect>
            </div>

            <div className={css.submitContainer}>
              <button type="button" className={css.backButton} onClick={handleBack}>
                <FormattedMessage id="CustomerSignupForms.previousButton" />
              </button>
              <button type="submit" className={css.submitButton} disabled={invalid}>
                <FormattedMessage id="CustomerSignupForms.nextButton" />
              </button>
            </div>
          </Form>
        );
      }}
    />
  );
};

export default compose(injectIntl)(CustomerSignupStep2Form);
